import React, { useState, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ChevronRight, ChevronLeft, Loader, MapPin, Settings, Globe } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import { useComponentMetrics, getMetricLabel } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
import { bornerLegende } from '../utils/legendeUtils';
/**
 * PianoGeographyChart - Composant pour afficher la geographie des visiteurs Piano Analytics
 * Design identique au composant GA4GeographyChart avec drill-down pays/regions/villes
 */


/*
 * ─── Les colonnes, décrites une fois ───────────────────────────────────────────────────────
 *
 * **Le tableau rendait ses colonnes dans un ordre figé**, recopié à chaque niveau de la
 * hiérarchie. Choisir le modèle « E-commerce » réglait bien *quelles* métriques afficher, mais
 * pas leur ordre : la configuration portait une suite, l'écran en imposait une autre.
 *
 * Elles sont donc décrites ici, et le rendu parcourt `componentMetrics.order` — comme le font
 * déjà les composants Google Analytics équivalents.
 */
const COLONNES = {
  visites: { champ: 'visits', format: (v, f) => f(v), variation: true },
  visiteurs: { champ: 'visitors', format: (v, f) => f(v), variation: true },
  rebond: { champ: 'bounceRate', format: (v, f) => `${f(v, 1)}%`, variation: true, inverse: true },
  pages_vues: { champ: 'pageViews', format: (v, f) => f(v), variation: true },
  taux_conv: { champ: 'conversionRate', format: (v, f) => `${f(v, 2)}%`, variation: true },
  conv: { champ: 'conversions', format: (v, f) => f(v), variation: true },
  valeur_conv: { champ: 'conversionsValue', format: (v, f) => `${f(v, 2)}\u20ac`, variation: true },
  duree_moy: { champ: 'avgTimeOnSite', duree: true },
  panier_moyen: { champ: 'avgOrderValue', format: (v, f) => `${f(v, 2)}\u20ac` },
  pages_visite: { champ: 'actionsPerVisit', format: (v, f) => f(v, 2) },
  transactions: { champ: 'transactions', format: (v, f) => f(v, 0), variation: true },
  chiffre_affaires: { champ: 'totalRevenue', format: (v, f) => `${f(v, 2)}\u20ac`, variation: true },
  taux_transaction: { champ: 'transactionRate', format: (v, f) => `${f(v, 2)}%` }
};

/** La durée, en minutes:secondes. */
const formaterDuree = (secondes) => {
  const minutes = Math.floor((secondes || 0) / 60);
  const reste = Math.floor((secondes || 0) % 60);
  return `${minutes}:${reste.toString().padStart(2, '0')}`;
};

const PianoGeographyChart = ({
  geography = [],
  previousGeography = [],
  compareEnabled = false,
  formatNumber,
  loading = false,
  // Props pour le drill-down
  siteId = null,
  token = null,
  period = 'LAST_30_DAYS',
  customStartDate = null,
  customEndDate = null,
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'piano_geography'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'visits', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const ITEMS_PER_PAGE = 10;

  // State pour le drill-down
  const [drillLevel, setDrillLevel] = useState('country'); // 'country', 'region', 'city'
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8', '#00C49F', '#FF6B6B', '#4ECDC4'];


  // Fonction pour afficher une variation
  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  // Hook pour les métriques du composant (réactif aux changements)
  const componentMetrics = useComponentMetrics('piano_geography', accountId);

  // Fonction helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  /**
   * L'intitulé d'une colonne, pris dans la table commune aux trois solutions de mesure.
   *
   * Les intitulés étaient écrits en dur — « Visites », « Visiteurs », « Pages/Visite ». Renommer
   * la table des libellés n'avait donc aucun effet ici, et le tableau continuait d'annoncer
   * autre chose que le reste de l'application : c'est ainsi que « Utilisateurs » restait
   * introuvable alors que la colonne était bien là.
   */
  const libelleColonne = (cle) => getMetricLabel(cle, 'piano_geography', accountId);

  /*
   * Les colonnes à rendre, **dans l'ordre du modèle**.
   *
   * `componentMetrics.order` porte la suite choisie ; `metrics` dit lesquelles sont actives.
   * Une clé inconnue de `COLONNES` vient d'un modèle conçu pour une autre famille : on l'écarte.
   */
  const colonnes = useMemo(() => {
    const ordre = componentMetrics.order?.length ? componentMetrics.order : componentMetrics.metrics;
    return (ordre || [])
      .filter((cle) => COLONNES[cle] && isMetricActive(cle))
      .map((cle) => ({ cle, ...COLONNES[cle] }));
  }, [componentMetrics.order, componentMetrics.metrics, isMetricActive]);


  /**
   * Ce que le camembert de droite représente.
   *
   * Il montrait les conversions quel que soit le tableau — y compris sur un compte e-commerce
   * où la colonne Conversions n'est pas affichée, et où ce sont les **transactions** qu'on
   * regarde. Le graphique et le tableau annonçaient alors deux choses différentes sans que rien
   * ne le dise.
   *
   * Les transactions l'emportent quand elles sont affichées — c'est le choix d'un compte
   * marchand, et le modèle « E-commerce » les active. À défaut, les conversions. Si aucune des
   * deux n'est à l'écran, le camembert disparaît : il n'aurait rien à représenter.
   */
  const mesureDuCamembert = isMetricActive('transactions')
    ? { cle: 'transactions', champ: 'transactions' }
    : (isMetricActive('conv') ? { cle: 'conv', champ: 'conversions' } : null);


  // Donnees a afficher selon le niveau de drill-down
  const displayData = drillData || geography;

  // Fonction pour charger les donnees de drill-down
  const loadDrillData = useCallback(async (level, country = null, region = null) => {
    if (!siteId || !token) {
      console.warn('[PianoGeo] siteId ou token manquant pour le drill-down');
      return;
    }

    setDrillLoading(true);
    try {
      const response = await axios.post(
        `${config.API_URL}/api/dashboard/piano/geography`,
        {
          siteId,
          period,
          customStartDate,
          customEndDate,
          level,
          country,
          region
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setDrillData(response.data.data);
        setDrillLevel(level);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('[PianoGeo] Erreur drill-down:', error);
    } finally {
      setDrillLoading(false);
    }
  }, [siteId, token, period, customStartDate, customEndDate]);

  // Clic sur un pays pour voir les regions
  const handleCountryClick = useCallback((countryName) => {
    if (!siteId || !token) return;
    setSelectedCountry(countryName);
    setSelectedRegion(null);
    loadDrillData('region', countryName, null);
  }, [siteId, token, loadDrillData]);

  // Clic sur une region pour voir les villes
  const handleRegionClick = useCallback((regionName) => {
    if (!siteId || !token || !selectedCountry) return;
    setSelectedRegion(regionName);
    loadDrillData('city', selectedCountry, regionName);
  }, [siteId, token, selectedCountry, loadDrillData]);

  // Retour au niveau precedent
  const handleBack = useCallback(() => {
    if (drillLevel === 'city') {
      // Retour aux regions
      setSelectedRegion(null);
      loadDrillData('region', selectedCountry, null);
    } else if (drillLevel === 'region') {
      // Retour aux pays
      setSelectedCountry(null);
      setSelectedRegion(null);
      setDrillData(null);
      setDrillLevel('country');
      setCurrentPage(1);
    }
  }, [drillLevel, selectedCountry, loadDrillData]);

  // Handler de tri
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Donnees triees
  const sortedGeography = useMemo(() => {
    if (!displayData || !displayData.length) return [];

    const sorted = [...displayData];
    sorted.sort((a, b) => {
      let aValue, bValue;

      // Cle de nom selon le niveau
      const nameKey = drillLevel === 'city' ? 'city' : (drillLevel === 'region' ? 'region' : 'country');

      if (sortConfig.key === 'country' || sortConfig.key === 'region' || sortConfig.key === 'city') {
        aValue = a[nameKey] || '';
        bValue = b[nameKey] || '';
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      if (sortConfig.key === 'conversionRate') {
        aValue = a.visits > 0 ? (a.conversions / a.visits * 100) : 0;
        bValue = b.visits > 0 ? (b.conversions / b.visits * 100) : 0;
      } else {
        aValue = Number(a[sortConfig.key]) || 0;
        bValue = Number(b[sortConfig.key]) || 0;
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [displayData, sortConfig, drillLevel]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!displayData || !displayData.length) return { visits: 0, visitors: 0, pageViews: 0, conversions: 0, conversionsValue: 0, bounceRate: 0, avgTimeOnSite: 0, avgOrderValue: 0 };

    const baseTotals = displayData.reduce((acc, item) => ({
      visits: acc.visits + (item.visits || 0),
      visitors: acc.visitors + (item.visitors || 0),
      pageViews: acc.pageViews + (item.pageViews || 0),
      conversions: acc.conversions + (item.conversions || 0),
      conversionsValue: acc.conversionsValue + (item.conversionsValue || 0),
      bounceRateSum: acc.bounceRateSum + ((item.bounceRate || 0) * (item.visits || 0)),
      avgTimeSum: acc.avgTimeSum + ((item.avgTimeOnSite || item.avgSessionDuration || 0) * (item.visits || 0))
    }), { visits: 0, visitors: 0, pageViews: 0, conversions: 0, conversionsValue: 0, bounceRateSum: 0, avgTimeSum: 0 });

    return {
      ...baseTotals,
      bounceRate: baseTotals.visits > 0 ? (baseTotals.bounceRateSum / baseTotals.visits) : 0,
      avgTimeOnSite: baseTotals.visits > 0 ? (baseTotals.avgTimeSum / baseTotals.visits) : 0,
      avgOrderValue: baseTotals.conversions > 0 ? (baseTotals.conversionsValue / baseTotals.conversions) : 0
    };
  }, [displayData]);

  // Calculate previous totals for comparison (only at country level without drill-down)
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousGeography || !previousGeography.length || drillLevel !== 'country') return null;

    const baseTotals = previousGeography.reduce((acc, item) => ({
      visits: acc.visits + (item.visits || 0),
      visitors: acc.visitors + (item.visitors || 0),
      pageViews: acc.pageViews + (item.pageViews || 0),
      conversions: acc.conversions + (item.conversions || 0),
      conversionsValue: acc.conversionsValue + (item.conversionsValue || 0),
      bounceRateSum: acc.bounceRateSum + ((item.bounceRate || 0) * (item.visits || 0)),
      avgTimeSum: acc.avgTimeSum + ((item.avgTimeOnSite || item.avgSessionDuration || 0) * (item.visits || 0))
    }), { visits: 0, visitors: 0, pageViews: 0, conversions: 0, conversionsValue: 0, bounceRateSum: 0, avgTimeSum: 0 });

    return {
      ...baseTotals,
      conversionRate: baseTotals.visits > 0 ? (baseTotals.conversions / baseTotals.visits * 100) : 0,
      bounceRate: baseTotals.visits > 0 ? (baseTotals.bounceRateSum / baseTotals.visits) : 0,
      avgTimeOnSite: baseTotals.visits > 0 ? (baseTotals.avgTimeSum / baseTotals.visits) : 0,
      avgOrderValue: baseTotals.conversions > 0 ? (baseTotals.conversionsValue / baseTotals.conversions) : 0
    };
  }, [compareEnabled, previousGeography, drillLevel]);

  // Donnees pour le pie chart Visites
  const visitsChartData = useMemo(() => {
    if (!displayData || !displayData.length) return [];

    const nameKey = drillLevel === 'city' ? 'city' : (drillLevel === 'region' ? 'region' : 'country');

    return displayData
      .slice(0, 8)
      .map(item => ({
        name: item[nameKey],
        value: item.visits || 0,
        percentage: totals.visits > 0 ? ((item.visits / totals.visits) * 100).toFixed(1) : 0
      }))
      .filter(item => parseFloat(item.percentage) > 0);
  }, [displayData, totals.visits, drillLevel]);

  // Donnees pour le pie chart Conversions

  /**
   * ⚠️ **Déclaré ici, et non avec `mesureDuCamembert` plus haut.**
   *
   * Le calcul lit `displayData`, défini au-dessus : placé avant, il lèverait une `ReferenceError`
   * à chaque rendu — et React démonte alors tout l'arbre, ce qui se voit comme une page
   * blanche. Ni le build ni la règle `no-use-before-define` ne l'empêchent par défaut.
   */
  /** Le total de la mesure représentée, calculé sur les lignes affichées. */
  const totalDuCamembert = useMemo(() => (
    mesureDuCamembert
      ? (displayData || []).reduce((somme, ligne) => somme + (ligne[mesureDuCamembert.champ] || 0), 0)
      : 0
  ), [displayData]);

  const conversionsChartData = useMemo(() => {
    if (!displayData || !displayData.length) return [];

    const nameKey = drillLevel === 'city' ? 'city' : (drillLevel === 'region' ? 'region' : 'country');

    return displayData
      .slice(0, 8)
      .map(item => ({
        name: item[nameKey],
        value: item[mesureDuCamembert.champ] || 0,
        percentage: totalDuCamembert > 0 ? (((item[mesureDuCamembert.champ] || 0) / totalDuCamembert) * 100).toFixed(1) : 0
      }))
      .filter(item => parseFloat(item.percentage) > 0);
  }, [displayData, totals.conversions, drillLevel]);

  // Donnees pour la legende
  const legendData = useMemo(() => {
    if (!displayData || !displayData.length) return [];

    const nameKey = drillLevel === 'city' ? 'city' : (drillLevel === 'region' ? 'region' : 'country');

    return displayData.slice(0, 8).map((item, index) => ({
      value: item[nameKey],
      color: COLORS[index % COLORS.length]
    }));
  }, [displayData, drillLevel]);

  // Dix étiquettes au plus : au-delà, la colonne s'étire sur plusieurs écrans et désigne des
  // parts invisibles. Le camembert et les totaux, eux, portent toujours sur tout.
  const { visibles: legendeVisible, reste: legendeReste } = bornerLegende(legendData);

  // Pagination
  const totalPages = Math.ceil((sortedGeography?.length || 0) / ITEMS_PER_PAGE);
  const paginatedGeography = useMemo(() => {
    if (!sortedGeography || !sortedGeography.length) return [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedGeography.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedGeography, currentPage]);

  // Titre selon le niveau
  const getLevelTitle = () => {
    if (drillLevel === 'city' && selectedRegion) {
      return `Villes de ${selectedRegion} (${selectedCountry})`;
    }
    if (drillLevel === 'region' && selectedCountry) {
      return `Regions de ${selectedCountry}`;
    }
    return 'Pays';
  };

  // Nom de colonne selon le niveau
  const getNameColumnLabel = () => {
    if (drillLevel === 'city') return 'Ville';
    if (drillLevel === 'region') return 'Region';
    return 'Pays';
  };

  if (loading) {
    return (
      <>
        <div className="section-header">
          <h4>
            <Globe size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Geographie
          </h4>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Chargement des donnees...
        </div>
      </>
    );
  }

  if (!geography || !geography.length) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ fontSize: '18px', fontWeight: '600' }}>
          <Globe size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Geographie
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Géographie Piano"
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '-' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Breadcrumb / Navigation drill-down */}
          {drillLevel !== 'country' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: '12px 16px',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px'
            }}>
              <button
                onClick={handleBack}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#374151'
                }}
              >
                <ChevronLeft size={16} />
                Retour
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', fontSize: '13px' }}>
                <MapPin size={14} />
                <span>{getLevelTitle()}</span>
              </div>
              {drillLoading && (
                <Loader size={16} style={{ animation: 'spin 1s linear infinite', marginLeft: 'auto' }} />
              )}
            </div>
          )}

          {/* Graphiques en secteurs cote a cote */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Pie chart Visites (gauche) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>
                      Visites par {getNameColumnLabel().toLowerCase()}
                    </h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={visitsChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {visitsChartData.map((_, index) => (
                            <Cell key={`visits-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value) + ' sessions', name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Legende commune (centre) */}
                  <div style={{
                    minWidth: '150px',
                    padding: '0 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    {legendeVisible.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: item.color,
                          marginRight: '8px',
                          borderRadius: '2px',
                          flexShrink: 0
                        }}></div>
                        <span>{item.value}</span>
                      </div>
                    ))}
                    {/* Une liste qui s'arrête en silence se lit comme une liste complète. */}
                    {legendeReste > 0 && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        + {legendeReste} autres
                      </div>
                    )}
                  </div>

                  {/* Pie chart Conversions (droite) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>
                      {mesureDuCamembert ? `${libelleColonne(mesureDuCamembert.cle)} par ${getNameColumnLabel().toLowerCase()}` : `Conversions par ${getNameColumnLabel().toLowerCase()}`}
                    </h5>
                    {totalDuCamembert > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={conversionsChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ percentage }) => `${percentage}%`}
                            outerRadius={70}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {conversionsChartData.map((_, index) => (
                              <Cell key={`conversions-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [`${formatNumber(value)} ${libelleColonne(mesureDuCamembert.cle).toLowerCase()}`, name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                        {/* Le modèle « Contenu » n'active ni l'une ni l'autre : le dire
                            vaut mieux qu'un cadre vide et sans titre. */}
                        {mesureDuCamembert
                          ? `Aucune ${libelleColonne(mesureDuCamembert.cle).toLowerCase()}`
                          : 'Activez Conversions ou Transactions pour ce graphique'}
                      </div>
                    )}
                </div>
              </div>
            </div>

          {/* Tableau detaille */}
          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
              <table
                className="devices-table"
                style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <th onClick={() => handleSort(drillLevel)} className="sortable" style={{ cursor: 'pointer' }}>
                      {getNameColumnLabel()} {sortConfig.key === drillLevel && (sortConfig.direction === 'asc' ? '\u25B2' : '\u25BC')}
                    </th>
                    {colonnes.map((colonne) => (
                      <th
                        key={colonne.cle}
                        onClick={() => handleSort(colonne.champ)}
                        className="sortable numeric"
                      >
                        {libelleColonne(colonne.cle)} {sortConfig.key === colonne.champ && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {paginatedGeography.map((item, index) => {
                    const globalIndex = (currentPage - 1) * ITEMS_PER_PAGE + index;
                    const nameKey = drillLevel === 'city' ? 'city' : (drillLevel === 'region' ? 'region' : 'country');
                    const itemName = item[nameKey] || 'Inconnu';
                    const canDrillDown = (drillLevel === 'country' || drillLevel === 'region') && siteId && token;
                    // Trouver l'élément correspondant dans les données de la période précédente (seulement au niveau pays)
                    const prev = compareEnabled && previousGeography && drillLevel === 'country'
                      ? previousGeography.find(p => p.country === itemName)
                      : null;

                    return (
                      <tr
                        key={`item-${index}`}
                        className={canDrillDown ? 'expandable-row' : ''}
                        onClick={() => {
                          if (drillLevel === 'country' && siteId && token) {
                            handleCountryClick(itemName);
                          } else if (drillLevel === 'region' && siteId && token) {
                            handleRegionClick(itemName);
                          }
                        }}
                        style={{
                          fontWeight: '500',
                          cursor: canDrillDown ? 'pointer' : 'default',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (canDrillDown) e.currentTarget.style.backgroundColor = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '';
                        }}
                      >
                        <td className="device-name" style={{ whiteSpace: 'nowrap' }}>
                          <span
                            className="device-indicator"
                            style={{ backgroundColor: COLORS[globalIndex % COLORS.length] }}
                          ></span>
                          {canDrillDown && (
                            <ChevronRight size={12} style={{ marginRight: '2px', verticalAlign: 'middle', color: '#9ca3af' }} />
                          )}
                          {itemName}
                        </td>
                        {colonnes.map((colonne) => (
                          <td key={colonne.cle} className="numeric">
                            {colonne.duree
                              ? formaterDuree(item[colonne.champ])
                              : colonne.format(item[colonne.champ] || 0, formatNumber)}
                            {colonne.variation && prev && renderVariation(
                              calculateVariation(item[colonne.champ] || 0, prev[colonne.champ] || 0),
                              colonne.inverse
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr className="total-row">
                    <td className="device-name"><strong>TOTAL</strong></td>
                    {colonnes.map((colonne) => (
                      <td key={colonne.cle} className="numeric">
                        <strong>{colonne.duree
                          ? formaterDuree(totals[colonne.champ])
                          : colonne.format(totals[colonne.champ] || 0, formatNumber)}</strong>
                        {colonne.variation && previousTotals && renderVariation(
                          calculateVariation(totals[colonne.champ] || 0, previousTotals[colonne.champ] || 0),
                          colonne.inverse
                        )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedGeography.length)} sur {sortedGeography.length} {getNameColumnLabel().toLowerCase()}s
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      Precedent
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </>
      )}

      {/* Modal de configuration des métriques */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="piano_geography"
          accountId={accountId}
          componentTitle="Géographie"
        />
      )}
    </>
  );
};

export default PianoGeographyChart;
